import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Minus, HelpCircle } from 'lucide-react';
import FaqIllustration from '../../assets/App_Img/partner_faq_3d.png';
import landingData from '../landingData.json';

const FaqSection = ({ openFaqIdx, setOpenFaqIdx }) => {
  return (
    <section className="py-24 bg-[#FAFAF8] relative overflow-hidden" id="faq">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8">
        
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-16 space-y-3">
          <div className="flex justify-center mb-2">
            <div 
              className="inline-flex items-center gap-2 text-[11px] font-extrabold tracking-widest uppercase px-4 py-1.5 rounded-full text-[#044E4D] bg-[#E0F2F1] border border-[#067978]/25"
            >
              <HelpCircle size={14} className="text-[#067978]" /> Questions &amp; answers
            </div>
          </div>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-extrabold text-[#0F172A] tracking-tight leading-tight">
            Everything you'd ask <br />
            <span className="text-[#067978]">before joining.</span>
          </h2>
        </div>

        {/* 2-Column Grid: Left Accordions, Right Illustration Card */}
        <div className="flex flex-col lg:flex-row gap-12 lg:gap-16 items-center">
          
          {/* Left Column: FAQ Accordion Cards */}
          <div className="flex-1 w-full space-y-3">
            {landingData.faqItems.map((faq, i) => {
              const isOpen = openFaqIdx === i;
              return (
                <div 
                  key={i} 
                  className={`bg-white border rounded-2xl overflow-hidden transition-all duration-300 ${
                    isOpen 
                      ? 'border-[#067978] shadow-md bg-[#f0fdfa]/40' 
                      : 'border-slate-200 hover:border-gray-300 hover:shadow-xs'
                  }`}
                >
                  <button 
                    onClick={() => setOpenFaqIdx(isOpen ? null : i)}
                    className="w-full p-5 sm:p-6 flex justify-between items-center text-left font-bold text-[#0F172A] text-base sm:text-lg select-none gap-4 border-none bg-transparent cursor-pointer"
                  >
                    <span className={isOpen ? 'text-[#067978]' : 'text-[#0F172A]'}>
                      {faq.q}
                    </span>
                    <div className="flex-shrink-0">
                      {isOpen ? (
                        <Minus size={20} className="text-[#067978]" />
                      ) : (
                        <Plus size={20} className="text-slate-400" />
                      )}
                    </div>
                  </button>

                  <AnimatePresence>
                    {isOpen && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3 }}
                        className="overflow-hidden"
                      >
                        <div className="px-5 sm:px-6 pb-6 text-sm sm:text-base text-slate-600 leading-relaxed border-t border-[#067978]/10 pt-4 font-normal">
                          {faq.a}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              );
            })}
          </div>

          {/* Right Column: Illustration Stage Card */}
          <div className="w-full max-w-xs sm:max-w-md lg:w-[480px] flex-shrink-0 flex items-center justify-center">
            <motion.div 
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="w-full aspect-square bg-gradient-to-br from-[#E0F2F1] via-[#E6F4F1] to-[#F0FDFA] rounded-[2rem] sm:rounded-[3rem] p-4 sm:p-8 flex items-center justify-center relative shadow-inner overflow-hidden border-4 border-white"
            >
              {/* Decorative Ambient Lighting Orbs */}
              <div className="absolute top-6 left-6 w-32 h-32 bg-white/50 rounded-full blur-xl pointer-events-none"></div>
              <div className="absolute bottom-6 right-6 w-32 h-32 bg-[#067978]/10 rounded-full blur-xl pointer-events-none"></div>

              {/* 3D Partner FAQ Illustration */}
              <img 
                src={FaqIllustration} 
                alt="Partner FAQ Onboarding" 
                className="w-[90%] max-h-[90%] object-contain mix-blend-multiply hover:scale-105 transition-transform duration-500 relative z-10"
              />
            </motion.div>
          </div>

        </div>

      </div>
    </section>
  );
};

export default FaqSection;
